import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  EmptyState,
  Gauge,
  Page,
  Stack,
  StatStrip,
  LightbulbIcon,
  ClockIcon,
  TargetIcon,
  SparkleIcon,
  TrendIcon,
  ShieldIcon,
  type Series,
  type Tone,
} from '@integra/ui';
import type { OptimisationKind } from '../api/types.js';
import { useOptimisation } from '../api/hooks.js';

const KIND_TONE: Record<OptimisationKind, Tone> = {
  timing: 'info',
  channel: 'info',
  creative: 'neutral',
  budget: 'warning',
  other: 'neutral',
};

function confidencePct(confidence: number): string {
  return `${String(Math.round(confidence * 100))}%`;
}

/** Confidence colour: strong advice reads garnet-to-green, weak reads amber. */
function confidenceSeries(confidence: number): Series {
  if (confidence >= 0.66) return 'success';
  if (confidence >= 0.33) return 'accent';
  return 'warning';
}

/**
 * Optimise (§10.3): HIVE intelligence for the workspace. INTEGRA sends HIVE the
 * channels and recent measured/estimated signals; HIVE returns recommendations —
 * when to post, which channel to lean on, what to try. Everything here is advisory
 * AI intelligence, tagged as HIVE-sourced and disclosed (Article 50), never blended
 * with the measured figures (§2.7). Dormant with a clear message until HIVE is wired.
 */
export function OptimisePage() {
  const { workspaceId = '' } = useParams();
  const optimise = useOptimisation(workspaceId);

  return (
    <Page
      title="Optimise"
      icon={LightbulbIcon}
      description="AI-driven optimisation recommendations from HIVE, based on your channels and recent signals."
      actions={
        <Button loading={optimise.isPending} onClick={() => optimise.mutate()}>
          Get recommendations
        </Button>
      }
      stats={
        optimise.data !== undefined && optimise.data.recommendations.length > 0 ? (
          <StatStrip
            stats={[
              {
                icon: LightbulbIcon,
                label: 'Recommendations',
                value: optimise.data.recommendations.length,
                tone: 'accent',
              },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Alert tone="info" title="Advisory intelligence, not a measured result">
          HIVE recommendations are AI-driven optimisation advice (EU AI Act Article 50). They are a
          starting point for a human decision, not channel-reported facts, and are never mixed into
          the measured figures on your dashboard.
        </Alert>

        {optimise.error instanceof Error && (
          <Alert tone="warning" title="HIVE is not available">
            {optimise.error.message}
          </Alert>
        )}

        {optimise.data !== undefined &&
          (optimise.data.recommendations.length === 0 ? (
            <EmptyState
              icon={LightbulbIcon}
              title="No recommendations right now"
              description="HIVE returned nothing for this workspace yet. Collect more signals (publish and measure), then try again."
              features={[
                { icon: ClockIcon, label: 'Best time to post per channel' },
                { icon: TargetIcon, label: 'Which channel to lean on' },
                { icon: SparkleIcon, label: 'Fresh creative angles to try' },
              ]}
            />
          ) : (
            <Stack gap="md">
              <p className="text-xs text-ink-muted">{optimise.data.disclosure}</p>
              {optimise.data.recommendations.map((rec, i) => (
                <Card key={`${rec.target}-${String(i)}`}>
                  <div className="flex items-center gap-5">
                    <div className="min-w-0 flex-1">
                      <Stack gap="sm">
                        <Cluster gap="sm" align="center">
                          <Badge tone={KIND_TONE[rec.kind]}>{rec.kind}</Badge>
                          <span className="font-heading text-sm text-navy-700">{rec.target}</span>
                          <Badge tone="neutral">HIVE</Badge>
                        </Cluster>
                        <p className="text-sm text-navy-700">{rec.action}</p>
                        {rec.rationale !== '' && (
                          <p className="text-xs italic text-ink-muted">{rec.rationale}</p>
                        )}
                      </Stack>
                    </div>
                    <div className="shrink-0">
                      <Gauge
                        value={rec.confidence}
                        label="confidence"
                        readout={confidencePct(rec.confidence)}
                        series={confidenceSeries(rec.confidence)}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </Stack>
          ))}

        {optimise.data === undefined && !optimise.isPending && optimise.error === null && (
          <EmptyState
            icon={SparkleIcon}
            title="Ask HIVE what to do next"
            description="Get optimisation recommendations tuned to this workspace's channels and recent performance."
            action={<Button onClick={() => optimise.mutate()}>Get recommendations</Button>}
            features={[
              { icon: TrendIcon, label: 'Tuned to recent performance' },
              { icon: TargetIcon, label: 'Channel-specific recommendations' },
              { icon: ShieldIcon, label: 'Advisory, never mixed with measured' },
            ]}
          />
        )}
      </Stack>
    </Page>
  );
}
